import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { AdminHeader } from "./adminShared";
import { Loader2, DollarSign, Star, Flag } from "lucide-react";

const cy = (c) => (c === "MYR" ? "RM" : (c || "RM") + " ");

export default function AdminPricing() {
  const [data, setData] = useState(null);
  const [rows, setRows] = useState({}); // key -> {price, active}
  const [slots, setSlots] = useState("0");
  const [enabled, setEnabled] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/pricing").then(({ data }) => {
    setData(data);
    const r = {};
    (data.billing_countries || []).forEach((c) => {
      const lk = `race_listing|${c.code}|12`;
      const le = data.pricing?.race_listing?.[c.code]?.["12"] || {};
      r[lk] = { price: le.price ?? "", active: le.active !== false };
      (data.featured_durations || [1, 3]).forEach((m) => {
        const fk = `featured_race|${c.code}|${m}`;
        const fe = data.pricing?.featured_race?.[c.code]?.[String(m)] || {};
        r[fk] = { price: fe.price ?? "", active: fe.active !== false };
      });
    });
    setRows(r);
    setSlots(String(data.featured_max_slots ?? 0));
    setEnabled(!!data.featured_enabled);
  });
  useEffect(() => { load(); }, []);

  const setRow = (k, patch) => setRows((p) => ({ ...p, [k]: { ...p[k], ...patch } }));

  const save = async () => {
    const entries = [];
    for (const [k, v] of Object.entries(rows)) {
      const [product, country, duration] = k.split("|");
      if (v.price === "" || isNaN(Number(v.price)) || Number(v.price) < 0) { toast.error("Enter valid non-negative prices for all rows"); return; }
      entries.push({ product, country, duration, price: Number(v.price), active: !!v.active });
    }
    setSaving(true);
    try {
      await api.put("/admin/pricing", { entries, featured_max_slots: Number(slots) || 0, featured_enabled: enabled });
      toast.success("Pricing saved — new purchases use these prices");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); } finally { setSaving(false); }
  };

  if (!data) return <div className="py-20 grid place-items-center"><Loader2 className="animate-spin text-[#22d3ee]" size={28} /></div>;
  const currency = data.processing_currency || "MYR";
  const countries = data.billing_countries || [];
  const durations = data.featured_durations || [1, 3];

  return (
    <div>
      <AdminHeader title="Pricing" subtitle="Prices are per billing country. All payments are processed in MYR via HitPay." />
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800 mb-6 max-w-3xl">
        Changing a price only affects <strong>new</strong> purchases. Existing paid transactions keep the amount paid at the time.
      </div>

      {/* Race Listing */}
      <div className="bg-white border border-slate-200 rounded-xl p-5 max-w-3xl mb-6" data-testid="pricing-listing">
        <div className="flex items-center gap-2 mb-1"><Flag size={18} className="text-[#06b6d4]" /><h3 className="font-display font-bold uppercase text-slate-800">Race Listing — 12 Months</h3></div>
        <p className="text-sm text-slate-500 mb-4">One annual payment per race listing.</p>
        <div className="space-y-3">
          {countries.map((c) => {
            const k = `race_listing|${c.code}|12`;
            const v = rows[k] || {};
            return (
              <div key={k} className="flex items-center gap-3 flex-wrap" data-testid={`row-${k}`}>
                <span className="w-28 font-semibold text-slate-700">{c.label}</span>
                <div className="flex items-center border border-slate-300 rounded-lg overflow-hidden focus-within:border-[#22d3ee]">
                  <span className="px-3 py-2.5 bg-slate-50 text-slate-500 text-sm font-semibold border-r border-slate-200">{cy(currency)}</span>
                  <input value={v.price} onChange={(e) => setRow(k, { price: e.target.value })} inputMode="decimal" data-testid={`price-${k}`} className="w-28 px-3 py-2.5 text-sm outline-none font-mono-data" />
                </div>
                <label className="inline-flex items-center gap-2 text-sm font-semibold cursor-pointer">
                  <input type="checkbox" checked={!!v.active} onChange={(e) => setRow(k, { active: e.target.checked })} data-testid={`active-${k}`} className="w-4 h-4" /> {v.active ? "Active" : "Inactive"}
                </label>
              </div>
            );
          })}
        </div>
      </div>

      {/* Featured Race */}
      <div className="bg-white border border-slate-200 rounded-xl p-5 max-w-3xl mb-6" data-testid="pricing-featured">
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-2"><Star size={18} className="text-amber-500" /><h3 className="font-display font-bold uppercase text-slate-800">Featured Race</h3></div>
          <label className="inline-flex items-center gap-2 text-sm font-semibold cursor-pointer">
            <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} data-testid="featured-enabled" className="w-4 h-4" /> {enabled ? "Enabled" : "Disabled"}
          </label>
        </div>
        <p className="text-sm text-slate-500 mb-4">Optional promotional upgrade, separate from the annual listing.</p>
        {countries.map((c) => (
          <div key={c.code} className="mb-4">
            <div className="font-semibold text-slate-700 mb-2">{c.label}</div>
            <div className="grid sm:grid-cols-2 gap-3">
              {durations.map((m) => {
                const k = `featured_race|${c.code}|${m}`;
                const v = rows[k] || {};
                return (
                  <div key={k} className="flex items-center gap-2 flex-wrap" data-testid={`row-${k}`}>
                    <span className="w-20 text-sm text-slate-600">{m === 1 ? "1 Month" : `${m} Months`}</span>
                    <div className="flex items-center border border-slate-300 rounded-lg overflow-hidden focus-within:border-[#22d3ee]">
                      <span className="px-2.5 py-2 bg-slate-50 text-slate-500 text-sm font-semibold border-r border-slate-200">{cy(currency)}</span>
                      <input value={v.price} onChange={(e) => setRow(k, { price: e.target.value })} inputMode="decimal" data-testid={`price-${k}`} className="w-24 px-2.5 py-2 text-sm outline-none font-mono-data" />
                    </div>
                    <label className="inline-flex items-center gap-1.5 text-xs font-semibold cursor-pointer">
                      <input type="checkbox" checked={!!v.active} onChange={(e) => setRow(k, { active: e.target.checked })} data-testid={`active-${k}`} className="w-4 h-4" /> {v.active ? "On" : "Off"}
                    </label>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
        <div className="border-t border-slate-100 pt-4 mt-2">
          <label className="text-xs font-semibold text-slate-600">Max Active Featured Races (0 = unlimited)</label>
          <input value={slots} onChange={(e) => setSlots(e.target.value)} inputMode="numeric" data-testid="pricing-slots" className="block w-32 border border-slate-300 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#22d3ee] mt-1 font-mono-data" />
        </div>
      </div>

      <button onClick={save} disabled={saving} data-testid="save-pricing" className="inline-flex items-center gap-2 bg-[#0a1420] text-white text-sm font-bold px-5 py-3 rounded-lg hover:bg-[#1a2740] disabled:opacity-40">
        {saving ? <Loader2 className="animate-spin" size={16} /> : <DollarSign size={16} />} Save All Pricing
      </button>
    </div>
  );
}
